import { Box, Divider, Grid2, Typography } from "@mui/material";
import { ActionGrid } from "./StyledComponents";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Lottie from "react-lottie";
import PurpleBlinkingLottie from "../../../Components/Lotties/PurpleBlinkingLottie.json";
import AlertHistoryComment from "./AlertHistoryComment";
import { useEffect, useState } from "react";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { toTitleCase } from "../../../Helper/Formatters/TextFormatter";

const PurpleLottie = () => (
  <Lottie
    options={{
      loop: true,
      autoplay: true,
      animationData: PurpleBlinkingLottie,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid slice",
      },
    }}
    height={25}
    width={25}
  />
);

const LoadingContainer = () => (
  <Box
    sx={{
      height: "calc(100vh - 285px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <CartLoader />
  </Box>
)

const NoActivityContainer = () => (
  <Box
    sx={{
      height: "calc(100vh - 285px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2.5,
        py: 1,
        bgcolor: "#F9F9F9",
        borderRadius: "16px",
      }}
    >
      <Typography variant="fw500s16lh20c000000" sx={{ color: "#87939D" }}>
        No activity on this alert yet
      </Typography>
    </Box>
  </Box>
)

const TimelineContainer = ({ timeline, instructionsMap }) => (
  <Grid2 container flexDirection={"column"} flexWrap={'nowrap'} sx={{ height: "calc(100vh - 285px)", overflowY: 'auto' }}>
    {timeline.map(({ date, events }) => (
      <Grid2 key={date} container flexDirection={"column"} gap={1}>
        <Typography variant="fw500s14c000000" sx={{ lineHeight: "20px" }}>
          {date}
        </Typography>

        <Timeline
          sx={{
            p: 0,
            m: 0,
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
        >
          {events.map((event, idx) => (
            <TimelineItem key={idx}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    px: 0,
                    py: 1,
                    m: 0,
                    bgcolor: "transparent",
                    boxShadow: "none",
                    border: 0,
                  }}
                >
                  {idx === 0 && date === timeline[0]?.date ? (
                    <PurpleLottie />
                  ) : (
                    <Box
                      sx={{
                        height: "25px",
                        width: "25px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <FiberManualRecordIcon sx={{ color: "#6D72F6" }} />
                    </Box>
                  )}
                </TimelineDot>

                {idx !== events.length - 1 && (
                  <TimelineConnector sx={{ width: "1px", backgroundColor: "#EAEAEA", my: '-15px' }} />
                )}
              </TimelineSeparator>

              <TimelineContent sx={{ py: 1, px: 1, display: "flex", gap: 2 }}>
                <Box sx={{ minWidth: "70px" }}>
                  <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: "24px" }}>
                    {event.time}
                  </Typography>
                </Box>

                <Grid2 container flexDirection={'column'} gap={2}>
                  {event.statusChanged ? (
                    <Grid2 container>
                      <Typography variant="fw400s14c494949" sx={{ lineHeight: "22px" }}>
                        {event.userName} updated status to "{event.status || (event.systemStatus?.toLowerCase() === 'pending' ? 'Under Observation' : toTitleCase(event.systemStatus))}"
                      </Typography>
                    </Grid2>
                  ) : (
                    event.systemStatus?.toLowerCase() === 'pending' && (
                      <Grid2 container>
                        <Typography variant="fw400s14c494949" sx={{ lineHeight: "22px" }}>
                          {event.userName} updated status to Under Observation
                        </Typography>
                      </Grid2>
                    )
                  )}
                  {event.comment && (
                    <Grid2 container flexDirection={"column"} gap={1}>
                      <Typography variant="fw400s14c494949" sx={{ lineHeight: "22px" }}>
                        {event.userName} commented:
                      </Typography>
                      <AlertHistoryComment comment={event.comment} />
                    </Grid2>
                  )}
                  {(event.checklistChecked?.length > 0 || event.checklistUnchecked?.length > 0) && (
                    <Grid2 container flexDirection={"column"} gap={1}>
                      {event.checklistChecked?.length > 0 && (
                        <Grid2 container flexDirection={"column"} gap={0.5}>
                          <Typography variant="fw400s14c494949" sx={{ lineHeight: "22px" }}>
                            {event.userName} marked the following actions as completed:
                          </Typography>
                          {event.checklistChecked.map(code => (
                            <Typography key={code} variant="fw400s14c494949" sx={{ lineHeight: "22px", pl: 1 }}>
                              • {instructionsMap[code] ?? code}
                            </Typography>
                          ))}
                        </Grid2>
                      )}
                      {event.checklistUnchecked?.length > 0 && (
                        <Grid2 container flexDirection={"column"} gap={0.5}>
                          <Typography variant="fw400s14c494949" sx={{ lineHeight: "22px" }}>
                            {event.userName} marked the following actions as pending:
                          </Typography>
                          {event.checklistUnchecked.map(code => (
                            <Typography key={code} variant="fw400s14c494949" sx={{ lineHeight: "22px", pl: 1 }}>
                              • {instructionsMap[code] ?? code}
                            </Typography>
                          ))}
                        </Grid2>
                      )}
                    </Grid2>
                  )}
                </Grid2>

              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Grid2>
    ))}
  </Grid2>
)

const IncidentActivityTimeline = ({ isLoading = false, activityHistory = [], instructionsMap = {} }) => {

  const [timeline, setTimeline] = useState([]);

  const getGroupedHistory = () => {
    // Sort all events chronologically (oldest first) to compare status changes
    const sortedHistory = [...activityHistory].sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time}`);
      const dateB = new Date(`${b.date} ${b.time}`);
      return dateA - dateB;
    });

    // Mark events where status actually changed
    let previousStatus = null;
    const historyWithStatusChange = sortedHistory.map((item) => {
      const statusChanged = item.status !== previousStatus;
      previousStatus = item.status;
      return { ...item, statusChanged };
    });

    // Group by date
    const grouped = {};
    historyWithStatusChange.forEach((item) => {
      if (!grouped[item.date]) grouped[item.date] = [];
      grouped[item.date].push(item);
    });

    // Sort each date's events by time (descending) for display
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => new Date(`1970/01/01 ${b.time}`) - new Date(`1970/01/01 ${a.time}`));
    });

    // Return as array sorted by date (latest first) for rendering
    return Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .map((date) => ({
        date,
        events: grouped[date],
      }));
  };

  useEffect(() => {
    if (activityHistory.length) {
      const groupedHistory = getGroupedHistory();
      setTimeline(groupedHistory);
    }
  }, [activityHistory])

  return (
    <ActionGrid container>
      <Typography variant="fw600s16c000000" sx={{ lineHeight: "20px" }}>
        Activity Tracker
      </Typography>
      <Divider sx={{ mr: -3, borderColor: "#EAEAEA" }} />
      {isLoading ? (
        <LoadingContainer />
      ) : (
        activityHistory.length === 0 ? (
          <NoActivityContainer />
        ) : (
          <TimelineContainer timeline={timeline} instructionsMap={instructionsMap} />
        )
      )}
    </ActionGrid>
  );
};

export default IncidentActivityTimeline;
